import { useLingui } from "@lingui/react";
import { Plural, Trans } from "@lingui/react/macro";
import {
  BellRing,
  CalendarDays,
  CircleCheck,
  CreditCard,
  LockOpen,
  Puzzle,
  Scissors,
  Users,
} from "lucide-react";
import type { ReactNode } from "react";
import type { PostCheckPromoCode } from "@/server-types";
import { getIndustryProof, TRUSTED_BY_COUNT } from "./industryProof";

type ValidPromoCode = Extract<
  PostCheckPromoCode["response"],
  { valid: true }
>["promoCode"];

export function ReferralSignupHeading() {
  const { i18n } = useLingui();
  const { stat } = getIndustryProof(null);

  return (
    <div
      data-identifier="referral-signup-intro"
      className="flex w-full flex-col items-center gap-4 text-center"
    >
      <p className="font-semibold text-xl md:text-2xl">
        <Trans>Naročanje in obveščanje strank</Trans>
      </p>
      <ul className="flex flex-col items-center gap-2 text-sm">
        <li className="flex items-center gap-2">
          <BellRing size={16} className="shrink-0 text-primary" />
          <span>
            <span className="font-semibold">{stat.value}</span>{" "}
            {i18n._(stat.label)}
          </span>
        </li>
        <li className="flex items-center gap-2">
          <CalendarDays size={16} className="shrink-0 text-primary" />
          <Trans>Stranke se naročijo same, 24/7</Trans>
        </li>
        <li className="flex items-center gap-2">
          <Puzzle size={16} className="shrink-0 text-primary" />
          <Trans>Uporabljaš le, kar potrebuješ</Trans>
        </li>
      </ul>
    </div>
  );
}

// Lime DS brand lime: 700 frame (5.9:1), 600 figure (3.8:1, large text), 800 label (8.8:1), WCAG AA on white.
// Inactive it renders only its children, in the same tree position, so falling back
// to the plain page doesn't remount the email input or Turnstile.
export function ReferralCoupon({
  active,
  promoCode,
  children,
}: {
  active: boolean;
  promoCode: ValidPromoCode | undefined;
  children: ReactNode;
}) {
  return (
    <div
      className={
        active
          ? "relative mt-3 flex w-full flex-col gap-3 rounded-[18px] border-2 border-[#466D51] border-dashed bg-white px-4 pt-5 pb-4"
          : "contents"
      }
    >
      {active && (
        <Scissors
          size={24}
          aria-hidden
          className="absolute -top-3 left-5 bg-white p-0.5 text-[#466D51]"
        />
      )}
      {/* Reserves the offer's height so the form doesn't jump when the code check returns. */}
      {active && (
        <div className="flex min-h-14 items-center justify-center gap-3.5">
          {promoCode && (
            <div
              data-identifier="referral-signup-discount"
              className="motion-safe:fade-in flex items-center gap-3.5 motion-safe:animate-in motion-safe:duration-300"
            >
              <span className="whitespace-nowrap font-extrabold text-[#548F68] text-[44px] tabular-nums leading-none tracking-tighter sm:text-[56px]">
                <Trans>
                  −{promoCode.discountPercent}
                  <span className="ml-0.5 text-[0.6em]">%</span>
                </Trans>
              </span>
              <span className="grid text-left text-[#34513D] text-lg leading-tight">
                <span className="font-bold">
                  <Trans>popusta</Trans>
                </span>
                <span className="font-medium">
                  <Trans>
                    za{" "}
                    <Plural
                      value={promoCode.discountMonths}
                      one="# mesec"
                      two="# meseca"
                      few="# mesece"
                      other="# mesecev"
                    />
                  </Trans>
                </span>
              </span>
            </div>
          )}
        </div>
      )}
      {children}
    </div>
  );
}

export function ReferralSignupReassurance() {
  return (
    <div className="flex flex-col items-center gap-3 text-center">
      <ul className="flex flex-wrap justify-center gap-x-3.5 gap-y-1 text-default-600 text-xs">
        <li className="flex items-center gap-1">
          <CircleCheck size={13} className="shrink-0 text-primary" />
          <Trans>Brezplačen preizkus</Trans>
        </li>
        <li className="flex items-center gap-1">
          <CreditCard size={13} className="shrink-0 text-primary" />
          <Trans>Brez kartice</Trans>
        </li>
        <li className="flex items-center gap-1">
          <LockOpen size={13} className="shrink-0 text-primary" />
          <Trans>Brez vezave</Trans>
        </li>
      </ul>
      <p className="flex items-center gap-2 text-sm">
        <Users size={16} className="shrink-0 text-primary" />
        <span>
          <Trans>
            Pridruži se{" "}
            <span className="font-semibold">{TRUSTED_BY_COUNT}</span> podjetjem
          </Trans>
        </span>
      </p>
    </div>
  );
}
